import {
  HardDriveDownload,
  ListChecks,
  LogIn,
  LogOut,
  Menu,
  Settings2,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useSession, useSessionActions } from "../admin/session.ts";
import { getApiMode } from "../api/config.ts";
import { useLocale } from "../i18n/index.ts";
import { offlineSupported } from "../offline/useSavedSpots.ts";
import { GlassSelect } from "./GlassSelect.tsx";
import { LanguageSelect } from "./LanguageSelect.tsx";

/** The initials of a name, for the avatar on the menu button. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

interface Props {
  lakeSlug: string;
  onLakeChange: (slug: string) => void;
  lakeOptions: readonly { value: string; label: string }[];
  onOpenSaved: () => void;
}

/**
 * Phones: one round glass button at the end of the top bar. It opens a glass panel with the things
 * that do not fit next to the search field: the account first (a prominent "Anmelden" (one login for
 * everybody), or the signed-in entries), then language and lake filter, then the offline list. The avatar initials show who is signed in.
 */
export function TopMenu({ lakeSlug, onLakeChange, lakeOptions, onOpenSaved }: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  const session = useSession();
  const actions = useSessionActions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const live = getApiMode() === "live";

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!panel.current?.contains(target) && !button.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const user = session.user;
  const label = user ? t("diver.menu", { name: user.name }) : t("menu.open");
  const close = () => setOpen(false);

  const item = (to: string, icon: ReactNode, text: string) => (
    <Link className="top-menu__item" to={to} onClick={close}>
      {icon}
      {text}
    </Link>
  );

  return (
    <div className="top-menu">
      <button
        ref={button}
        type="button"
        className="ds-btn ds-btn--icon ds-btn--glass ds-glass"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {user ? (
          <span className="top-menu__avatar" aria-hidden="true">
            {initialsOf(user.name)}
          </span>
        ) : (
          <Menu aria-hidden="true" strokeWidth={1.75} />
        )}
      </button>
      {open ? (
        <div
          ref={panel}
          className="top-menu__panel ds-glass ds-glass--solid"
          role="dialog"
          aria-labelledby={titleId}
        >
          <div className="top-menu__head">
            <h2 id={titleId} className="ds-text-headline m-0">
              {t("menu.title")}
            </h2>
            <button
              type="button"
              className="ds-btn ds-btn--icon ds-btn--plain"
              aria-label={t("menu.close")}
              onClick={() => {
                close();
                button.current?.focus();
              }}
            >
              <X aria-hidden="true" strokeWidth={1.75} />
            </button>
          </div>

          {live && session.status !== "loading" ? (
            <nav className="top-menu__list" aria-label={t("diver.nav")}>
              {user ? (
                <>
                  {item(
                    `/${locale}/konto`,
                    <UserRound aria-hidden="true" size={18} strokeWidth={1.75} />,
                    t("diver.nav_account"),
                  )}
                  {session.staff
                    ? item(
                        `/${locale}/admin/spots`,
                        <Settings2 aria-hidden="true" size={18} strokeWidth={1.75} />,
                        t("admin.menu"),
                      )
                    : item(
                        `/${locale}/konto/vorschlaege`,
                        <ListChecks aria-hidden="true" size={18} strokeWidth={1.75} />,
                        t("diver.nav_suggestions"),
                      )}
                  <button
                    type="button"
                    className="top-menu__item"
                    onClick={async () => {
                      close();
                      await actions.signOutAndClear();
                      void navigate(`/${locale}/`);
                    }}
                  >
                    <LogOut aria-hidden="true" size={18} strokeWidth={1.75} />
                    {t("diver.signOut")}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    className="ds-btn ds-btn--filled top-menu__signin"
                    to={`/${locale}/login`}
                    onClick={close}
                  >
                    <LogIn aria-hidden="true" strokeWidth={1.75} />
                    {t("diver.signIn")}
                  </Link>
                </>
              )}
            </nav>
          ) : null}

          <div className="top-menu__field">
            <span className="ds-text-footnote text-label-secondary">{t("language.label")}</span>
            <LanguageSelect />
          </div>
          <div className="top-menu__field">
            <span className="ds-text-footnote text-label-secondary">{t("lakeFilter.label")}</span>
            <GlassSelect
              value={lakeSlug}
              onChange={onLakeChange}
              options={lakeOptions}
              label={t("lakeFilter.label")}
            />
          </div>

          {offlineSupported() ? (
            <button
              type="button"
              className="top-menu__item"
              onClick={() => {
                close();
                onOpenSaved();
              }}
            >
              <HardDriveDownload aria-hidden="true" size={18} strokeWidth={1.75} />
              {t("offline.listTitle")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
